import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { ciclesService, Cicle } from '../services/api';
import CicleModal from '../components/CicleModal';
import ConfirmDialog from '../components/ConfirmDialog';

function CiclesPage() {
  const [cicles, setCicles] = useState<Cicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [cicleToEdit, setCicleToEdit] = useState<Cicle | null>(null);
  const [cicleToDelete, setCicleToDelete] = useState<Cicle | null>(null);

  const carregarCicles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ciclesService.getAll();
      setCicles(data);
    } catch (error) {
      console.error('Error carregant cicles:', error);
      alert('Error en carregar els cicles formatius.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarCicles();
  }, [carregarCicles]);

  const handleCreate = () => {
    setCicleToEdit(null);
    setModalOpen(true);
  };

  const handleEdit = (cicle: Cicle) => {
    setCicleToEdit(cicle);
    setModalOpen(true);
  };

  const handleDeleteClick = (cicle: Cicle) => {
    setCicleToDelete(cicle);
  };

  const handleSave = async (data: { nom: string; descripcio: string; actiu: boolean; nombre_cursos: number }) => {
    try {
      if (cicleToEdit) {
        await ciclesService.update(cicleToEdit.id, data);
      } else {
        await ciclesService.create(data);
      }
      setModalOpen(false);
      setCicleToEdit(null);
      await carregarCicles();
    } catch (error) {
      console.error('Error guardant cicle:', error);
      alert('Error en guardar el cicle formatiu.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!cicleToDelete) return;

    try {
      await ciclesService.delete(cicleToDelete.id);
      setCicleToDelete(null);
      await carregarCicles();
    } catch (error) {
      console.error('Error eliminant cicle:', error);
      alert('Error en eliminar el cicle formatiu.');
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(cicles);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setCicles(items);

    try {
      await ciclesService.reorder(items);
    } catch (error) {
      console.error('Error reordenant cicles:', error);
      await carregarCicles();
    }
  };

  if (loading) {
    return <div className="page-container"><div className="loading">Carregant...</div></div>;
  }

  return (
    <div className="page-container">
      <div className="table-container">
        <div className="table-header">
          <h2>Cicles Formatius</h2>
          <button className="btn btn-primary" onClick={handleCreate}>
            + Nou cicle
          </button>
        </div>

        {cicles.length === 0 ? (
          <div className="empty-state">
            <p>No hi ha cicles formatius. Crea'n un de nou!</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="cicles-list">
              {(provided) => (
                <table ref={provided.innerRef} {...provided.droppableProps}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th>Nom</th>
                      <th>Descripció</th>
                      <th>Cursos</th>
                      <th>Estat</th>
                      <th>Accions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cicles.map((cicle, index) => (
                      <Draggable key={cicle.id} draggableId={String(cicle.id)} index={index}>
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={snapshot.isDragging ? 'dragging' : ''}
                          >
                            <td {...provided.dragHandleProps} className="drag-handle-cell">
                              <span className="drag-handle">⠿</span>
                            </td>
                            <td><strong>{cicle.nom}</strong></td>
                            <td>{cicle.descripcio}</td>
                            <td>{cicle.nombre_cursos}</td>
                            <td>
                              <span style={{
                                color: cicle.actiu ? 'var(--color-success)' : '#999',
                                fontWeight: 600
                              }}>
                                {cicle.actiu ? 'Actiu' : 'Inactiu'}
                              </span>
                            </td>
                            <td>
                              <div className="actions-cell">
                                <button
                                  className="btn-icon edit"
                                  title="Editar"
                                  onClick={() => handleEdit(cicle)}
                                >
                                  ✏️
                                </button>
                                <button
                                  className="btn-icon delete"
                                  title="Eliminar"
                                  onClick={() => handleDeleteClick(cicle)}
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                </table>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      <CicleModal
        isOpen={modalOpen}
        cicle={cicleToEdit}
        onClose={() => { setModalOpen(false); setCicleToEdit(null); }}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={!!cicleToDelete}
        title="Eliminar cicle"
        message={`Estàs segur que vols eliminar el cicle "${cicleToDelete?.nom}"? Aquesta acció no es pot desfer.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setCicleToDelete(null)}
      />
    </div>
  );
}

export default CiclesPage;
